import { UIModalController } from '../modal/ui-modal-controller';
import '../option/ui-option-account';
import '../option/ui-option-basic';
import '../option/ui-option-color';
import '../option/ui-option-dial';
import '../option/ui-option-icon';
import '../option/ui-option-lang';
import '../option/ui-option-multipayment';
import '../option/ui-option-plain';
import '../option/ui-option';
import { UIElement } from '../ui-element';
import { UIModal } from '../modal/ui-modal';
import { keyCodes, TabIndex } from '../../global/keyboard';
import { Digest } from '../../global/digest';
import { Labels } from '../../global/labels';

import {
    makePopoverHandler,
    debounce,
    appendModalControllerIfRequired,
    fitsHorizontally,
} from '../../global/helpers';
import {
    addOutsideClickHandler,
    dispatchNativeEvent,
    hide,
    insertElements,
    prerenderElementsTree,
    rebuild,
    show,
    updateClassList,
    setNativeInputValue,
    warnForAttributeDeprecation,
} from '../../global/ui-helpers';
import { getDebugMode } from '../../global/config';
import { browser } from '../../global/browser';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { CustomFormComponent } from '../../types/custom-form-component';
import type { TranslationLabels } from '../../types/labels.type';
import type { UIModalWidthCompact } from '../modal/ui-modal.types';
import type {
    Attributes,
    AttributeValue,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import type { Debounce, NullOr, UndefinedOr } from '../../global/utils.type';
import type {
    UIDropdownLayout,
    UIDropdownLayouts,
    UIDropdownPopoverWidths,
    UIDropdownOptionFactory,
} from './ui-dropdown.type';
import type { UIField } from '../field/ui-field';
import styles from './ui-dropdown.css';
import { Deprecated } from '../../decorators/deprecated.decorator';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIDropdown
 * @element ui-dropdown
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-dropdown</code> element.
 * Create custom select dropdown with different types.
 * @property {UIDropdownLayout} [layout="basic"] Type of the option.
 *  {@desc account}
 *  {@desc basic}
 *  {@desc color}
 *  {@desc dial}
 *  {@desc icon}
 *  {@desc lang}
 *  {@desc multipayment}
 *  {@desc plain}
 * @property {boolean} [mobileNative] If true on mobile
 * it will turn to native select.
 * @property {boolean} [hideSelected] If true,
 * selected option will be hidden in dropdown.
 * @property {boolean} [expanded] If true, dropdown will be expanded.
 * @property {string} [titleOpen] Deprecated: use <code>aria-label</code>
 * on the <code>ui-dropdown</code> host to name the control button instead. The selected
 * value is appended to that name automatically.
 * @property {number} [maxlength] Maximum number of options used to determine the popover height before scrolling.
 * @property {string} [glyphColor] Color of glyph for lang-layout.
 * @property {string} [controlId] Control (input element) id. Deprecated! Instead of using control-id, please set id directly on the native select element instead. If no id is set, a random id is generated.
 * @property {string} [value] Value of the field.
 * @property {boolean} [disabled] If true, dropdown will be disabled.
 * @property {UIModalWidthCompact} [popoverWidth] Fixed width of the popover.
 * Only applicable to <code>dial</code>, <code>lang</code>, and <code>plain</code> layouts.
 * @property {number} popoverHOffset Popover horizontal offset.
 * @property {number} popoverVOffset Popover vertical offset.
 * @property {HTMLButtonElement} control Shortcut to control element.
 * @property {HTMLSelectElement} nativeSelect Shortcut to select element.
 * @property {HTMLDivElement} customSelect Shortcut to custom select element.
 * @property {HTMLDivElement} popoverElement Shortcut to popover element.
 * @property {HTMLUListElement} options Shortcut to options element.
 * @slot
 * @example
 * <ui-dropdown aria-label="Select currency">
 *  <select id="custom-dropdown-id" aria-label="Available currencies">
 *    <option>First</option>
 *    <option>Second</option>
 *  </select>
 * </ui-dropdown>
 */

@Component({ tag: 'ui-dropdown', styles })
export class UIDropdown extends UIElement implements CustomFormComponent {
    static readonly POPOVER_V_OFFSET: number = 0;
    static readonly POPOVER_H_OFFSET: number = 0;

    private static readonly LISTBOX_ID_PREFIX = 'ui-dropdown-';
    private static readonly CONTROL_BUTTON_ID_SUFFIX = '-button';
    private static readonly CONTROL_NAME_ID_SUFFIX = '-name';
    private static readonly VALUE_ID_SUFFIX = '-value';
    private static readonly FIELD_LABEL_ID_SUFFIX = '-label';
    private static readonly VIEWPORT_BOTTOM_MARGIN = 10;

    /**
     * Width of popover
     */
    static readonly PopoverWidth: UIDropdownPopoverWidths = {
        Small: 'small',
        Medium: 'medium',
    };

    static get observedAttributes(): ObservedAttributes {
        return ['expanded', 'option', 'control-id'];
    }

    /**
     * Layouts of dropdown.
     * @type {Record<string, string>}
     */
    static readonly layouts: UIDropdownLayouts = {
        ACCOUNT: 'account',
        BASIC: 'basic',
        COLOR: 'color',
        DIAL: 'dial',
        ICON: 'icon',
        LANG: 'lang',
        MULTIPAYMENT: 'multipayment',
        PLAIN: 'plain',
    };

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-dropdown', {
            openLang: 'Open language select',
            countryCode: 'Country code',
            listboxName: 'Available options',
            eng: 'English',
            est: 'Estonian',
            lat: 'Latvian',
            lit: 'Lithuanian',
            rus: 'Russian',
        });
    }

    @Attribute(String) layout: UIDropdownLayout = UIDropdown.layouts.BASIC;
    @Attribute(Boolean) mobileNative?: boolean;
    @Attribute(Boolean) hideSelected?: boolean;
    @Attribute(Boolean) expanded?: boolean;
    @Deprecated(
        'set aria-label on host for an accessible name instead. Kept for backward compatibility'
    )
    @Attribute(String)
    titleOpen?: string;
    @Attribute(Number) maxlength?: number;
    @Attribute(String) glyphColor?: string;
    @Deprecated(
        'set id directly on the native select element instead. If no id is set, a random id is generated.'
    )
    @Attribute(String)
    controlId?: string;
    @Attribute(Boolean) disabled?: boolean;
    @Attribute(String) popoverWidth: UIModalWidthCompact =
        UIDropdown.PopoverWidth.Small;

    @Child('.ui-dropdown__control') control!: HTMLButtonElement;
    @Child('.ui-dropdown__options') options!: HTMLUListElement;
    @Child('.ui-dropdown__select') nativeSelect!: HTMLSelectElement;
    @Child('.ui-dropdown__custom') customSelect!: HTMLDivElement;

    private _id?: string;
    private _inputValue?: string;
    private _detachedPopover: NullOr<HTMLDivElement> = null;
    private _popoverModalToClose: NullOr<UIModal> = null;
    private _popoverModalToCloseHandler: NullOr<EventListener> = null;
    private _hasUserSetNativeId?: boolean;
    private _dropdownOwnedNativeId?: string;
    private observer?: MutationObserver;
    private outsideClickRemover?: () => void;
    private clearInputValueDebounce!: Debounce;
    private nativeSelectForm?: HTMLFormElement;
    private outsideClickHandlerRequestId = 0;
    private readonly optionClickHandler = this.handleClickOnOption.bind(this);
    private readonly nativeSelectChangeHandler =
        this.handleNativeSelectChange.bind(this);
    private readonly keyDownHandler = this.handleKeyDown.bind(this);
    private readonly toggleDropdownPopoverHandler =
        this.toggleDropdownPopover.bind(this);
    private readonly appearanceChangeHandler =
        this.onChangeAppearance.bind(this);
    private readonly formResetHandler = this.handleFormReset.bind(this);
    private readonly controlMutationsHandler =
        this.handleControlMutations.bind(this);
    private readonly outsideClickHandler = this.handleClickOutside.bind(this);

    /**
     * Returns a randomly generated id that remains stable for this instance.
     * @readonly
     */
    get uid(): string {
        if (!this._id) {
            this._id = Digest.randomId();
        }
        return this._id;
    }

    /** Resolves the native select in both pre- and post-render states. */
    private resolveNativeSelect(): NullOr<HTMLSelectElement> {
        return (
            this.nativeSelect || // after render (cached @Child)
            this.querySelector<HTMLSelectElement>('.ui-dropdown__select') || // after render
            this.querySelector<HTMLSelectElement>('select') // before render
        );
    }

    /**
     * Assigns an ID to the native select and records it as dropdown-owned.
     * "Dropdown-owned" means that the ID was either generated or came from control-id set by user.
     * @param {HTMLSelectElement} select
     * @param {string} id
     * @private
     */
    private setDropdownOwnedNativeId(
        select: HTMLSelectElement,
        id: string
    ): void {
        this._dropdownOwnedNativeId = id;
        select.setAttribute('id', id);
    }

    /**
     * Removes the dropdown-owned ID from the native select and clears its record.
     * @param {HTMLSelectElement} select
     * @private
     */
    private removeDropdownOwnedNativeId(select: HTMLSelectElement): void {
        delete this._dropdownOwnedNativeId;
        select.removeAttribute('id');
    }

    /**
     * Returns the control id for ui-field (label[for], aria-describedby).
     * Priority: native select id > control-id > uid.
     * @returns {string}
     */
    getControlIdForField(): string {
        const select = this.resolveNativeSelect();
        if (select) {
            const id = select.getAttribute('id')?.trim();
            if (id) {
                this._hasUserSetNativeId ??= id !== this._dropdownOwnedNativeId;
                return id;
            }
            const controlIdAttr =
                this.getAttribute('control-id') || this.controlId || null;
            if (controlIdAttr) {
                this._hasUserSetNativeId ??= false;
                this.setDropdownOwnedNativeId(select, controlIdAttr);
                return controlIdAttr;
            }
            const effectiveId = select.id || this.uid;
            this._hasUserSetNativeId ??= false;
            this.setDropdownOwnedNativeId(select, effectiveId);
            return effectiveId;
        }
        return this.getAttribute('control-id') || this.controlId || this.uid;
    }

    /**
     * Returns the id of the listbox (options) element.
     * @private
     * @returns {string}
     */
    private getListboxId(): string {
        return UIDropdown.LISTBOX_ID_PREFIX + this.getControlIdForField();
    }

    /**
     * Updates ids and references across the component based on a passed control id.
     * @private
     * @param {string} controlId
     */
    private syncControlIdReferences(controlId: string): void {
        const listboxId = UIDropdown.LISTBOX_ID_PREFIX + controlId;

        this.control?.setAttribute(
            'id',
            `${controlId}${UIDropdown.CONTROL_BUTTON_ID_SUFFIX}`
        );
        this.control?.setAttribute('aria-controls', listboxId);
        this.options?.setAttribute('id', listboxId);
    }

    /**
     * Synchronizes control ID references, field labels, and ARIA references for
     * dropdowns in the current field.
     * @private
     */
    private syncFieldControlIdReferences(): void {
        const controlId = this.getControlIdForField();
        const field = this.closest<UIField>('ui-field');

        this.syncControlIdReferences(controlId);
        field?.linkLabels();
        this.wireAriaForSiblingDropdowns(field);
    }

    /**
     * Synchronizes field ARIA references for this dropdown and its sibling dropdowns.
     * @param {UIField | null} [field]
     * @private
     */
    private wireAriaForSiblingDropdowns(field?: NullOr<UIField>): void {
        const dropdowns = field
            ? Array.from(field.querySelectorAll<UIDropdown>('ui-dropdown'))
            : [this];

        // Siblings may not be upgraded yet while this dropdown hydrates; they
        // wire their own ARIA references once they hydrate.
        dropdowns.forEach((dropdown) => {
            if (dropdown instanceof UIDropdown) {
                dropdown.syncFieldAriaReferences();
            }
        });
    }

    /**
     * Gets or sets the value of the native select.
     */
    get value(): string {
        return this.nativeSelect.value;
    }

    set value(value: string) {
        const select = this.nativeSelect;
        if (select.value === value) {
            return;
        }
        setNativeInputValue(select, value);
        this.onChangeValue();
    }

    /**
     * Gets or sets the index of the selected option in the native select.
     * @type {number}
     */
    get selectedIndex(): number {
        return this.nativeSelect.selectedIndex;
    }

    set selectedIndex(value: number) {
        const select = this.nativeSelect;
        if (
            select.selectedIndex !== value &&
            value > -1 &&
            value < select.options.length
        ) {
            select.selectedIndex = value;
            this.onChangeValue();
        }
    }

    /**
     * Returns the detached popover when present, or the rendered popover element.
     * @readonly
     */
    get popoverElement(): HTMLDivElement {
        if (this._detachedPopover) {
            return this._detachedPopover;
        }
        return this.querySelector<HTMLDivElement>('.ui-dropdown__popover')!;
    }

    /**
     * Gets the option element tag name for the current layout, or sets the layout from an option element tag name.
     * @returns {string}
     */
    get option(): `${string}-${string}` {
        return `ui-option-${this.layout}`;
    }

    set option(value: string) {
        if (!value?.startsWith('ui-option-')) {
            this.layout = '' as UIDropdownLayout;
        }
        this.layout = value.replace('ui-option-', '') as UIDropdownLayout;
    }

    /**
     * Returns the custom element factory for the current layout's option element.
     * @private
     * @returns {UndefinedOr<UIDropdownOptionFactory>}
     */
    private getOptionFactory(): UndefinedOr<UIDropdownOptionFactory> {
        return customElements.get(
            this.option
        ) as UndefinedOr<UIDropdownOptionFactory>;
    }

    /**
     * Gets or sets the readonly state of the slotted select.
     */
    get readonly(): boolean {
        return this.nativeSelect.hasAttribute('readonly');
    }

    set readonly(value: boolean) {
        if (value) {
            this.nativeSelect.setAttribute('readonly', '');
        } else {
            this.nativeSelect.removeAttribute('readonly');
        }
    }

    /**
     * Returns the horizontal popover offset defined by the current layout's ui-option-* element, or the default offset.
     * @readonly
     */
    get popoverHOffset(): number {
        return (
            this.getOptionFactory()?.POPOVER_H_OFFSET ??
            UIDropdown.POPOVER_H_OFFSET
        );
    }

    /**
     * Returns the vertical popover offset defined by the current layout's ui-option-* element, or the default offset.
     * @readonly
     */
    get popoverVOffset(): number {
        return (
            this.getOptionFactory()?.POPOVER_V_OFFSET ??
            UIDropdown.POPOVER_V_OFFSET
        );
    }

    /**
     * Gets or sets the temporary type-ahead search value.
     */
    get inputValue(): string {
        return this._inputValue || '';
    }

    set inputValue(value: string) {
        this._inputValue = value;
        this.clearInputValueDebounce();
    }

    /**
     * Returns the modal containing the detached popover.
     * @readonly
     */
    get modal(): NullOr<UIModal> {
        return this._detachedPopover?.closest<UIModal>('ui-modal') || null;
    }

    /**
     * Removes the pending popover modal update listener and clears its references.
     * @private
     */
    private removePopoverModalListener(): void {
        if (this._popoverModalToClose && this._popoverModalToCloseHandler) {
            this._popoverModalToClose.removeEventListener(
                'modal-updatelist',
                this._popoverModalToCloseHandler
            );
        }

        this._popoverModalToClose = null;
        this._popoverModalToCloseHandler = null;
    }

    /**
     * Check if mobile mode enabled.
     * @deprecated Use browser.mobileMediaMatches() directly.
     * @returns {boolean}
     */
    isMobile(): boolean {
        return browser.mobileMediaMatches();
    }

    /**
     *  Returns all or specific option.
     *  @param {number} [index]
     *  @returns {HTMLLIElement|NodeListOf<HTMLLIElement>|undefined}
     */
    getOptions(
        index = -1
    ): UndefinedOr<NodeListOf<HTMLLIElement> | HTMLLIElement> {
        return index > -1
            ? this.getVisibleOptionAt(index)
            : this.getVisibleOptions();
    }

    /**
     * Returns all currently visible option list items.
     * @private
     * @returns {NodeListOf<HTMLLIElement>}
     */
    private getVisibleOptions(): NodeListOf<HTMLLIElement> {
        return this.popoverElement.querySelectorAll<HTMLLIElement>(
            '.ui-dropdown__option:not(.-hidden)'
        );
    }

    /**
     * Returns the visible option list item at the given index, if any.
     * @param {number} index
     * @private
     * @returns {UndefinedOr<HTMLLIElement>}
     */
    private getVisibleOptionAt(index: number): UndefinedOr<HTMLLIElement> {
        return this.getVisibleOptions()[index];
    }

    private setConfigId(config: ElementConfig, id: string): void {
        config.attributes = { ...config.attributes, id };
    }

    /**
     * Returns active option. If active option is hidden then undefined.
     * @returns {HTMLLIElement|undefined}
     */
    getActiveOption(): UndefinedOr<HTMLLIElement> {
        return [...this.getVisibleOptions()].find((opt) =>
            opt.classList.contains('-selected')
        );
    }

    /**
     * Returns focused option, if no focused options then undefined.
     * @returns {HTMLLIElement|undefined}
     */
    getFocusedOption(): UndefinedOr<HTMLLIElement> {
        return [...this.getVisibleOptions()].find(
            (opt) => opt === document.activeElement
        );
    }

    /**
     * Get popover height in pixels.
     * @returns {number}
     */
    getPopoverHeight(): number {
        let height = 0;
        const options = this.popoverElement.querySelectorAll(
            '.ui-dropdown__option:not(.-hidden), [optgroup]'
        );
        const limit = this.maxlength
            ? Math.min(this.maxlength, options.length)
            : options.length;

        for (let i = 0; i < limit; i++) {
            if (
                window.innerHeight - UIDropdown.VIEWPORT_BOTTOM_MARGIN <
                height + options[i].clientHeight
            ) {
                break;
            }
            height = height + options[i].clientHeight + (i + 1 < limit ? 1 : 0);
        }
        return height > 0 ? height : 0;
    }

    /**
     * Sets popover height in pixels.
     * @param {number} value
     */
    setPopoverHeight(value: number): void {
        const optionsEl = this.popoverElement.querySelector<HTMLUListElement>(
            '.ui-dropdown__options'
        );
        if (!optionsEl) {
            return;
        }
        optionsEl.style.maxHeight = value > 0 ? `${value}px` : '';
    }

    /**
     * Sets popover width in pixels.
     * @param {number} value
     */
    setPopoverWidth(value: number): void {
        this.popoverElement.style.width = value > 0 ? `${value}px` : '';
    }

    /**
     * Opens detached popover.
     * @fires event:modal-open
     */
    openDetachedPopover(): void {
        if (this._detachedPopover) {
            return;
        }
        this._detachedPopover = this.popoverElement;
        this.dispatchCustomEvent('modal-open', {
            type: UIModal.Type.Menu,
            content: this.popoverElement,
            params: {
                disableKeyboardTrapping: true,
                // Prevent modal from announcing as dialog - dropdown uses listbox pattern
                attributes: {
                    'role': 'presentation',
                    'no-bottom-sheet': true,
                    'compact': true,
                },
                classList: {
                    '-dropdown': true,
                },
                position: makePopoverHandler(this.control, {
                    popoverHeight: () => {
                        this.setPopoverHeight(this.getPopoverHeight());
                        return this.popoverElement.offsetHeight;
                    },
                    verticalOffset: this.popoverVOffset,
                    popoverWidth: () => {
                        if (
                            this.layout === UIDropdown.layouts.ACCOUNT &&
                            !browser.mobileMediaMatches()
                        ) {
                            this.setPopoverWidth(
                                Math.max(
                                    UIModal.WindowWidth.medium,
                                    this.offsetWidth
                                )
                            );
                        } else if (
                            this.parentElement?.localName === 'ui-inputgroup' &&
                            this.hasCombinedInputSibling()
                        ) {
                            this.setPopoverWidth(
                                this.parentElement.getBoundingClientRect().width
                            );
                        } else if (
                            this.popoverWidth &&
                            (
                                [
                                    UIDropdown.layouts.DIAL,
                                    UIDropdown.layouts.LANG,
                                    UIDropdown.layouts.PLAIN,
                                ] as Array<string>
                            ).includes(this.layout)
                        ) {
                            const width =
                                UIModal.WindowWidth[this.popoverWidth];
                            this.setPopoverWidth(
                                fitsHorizontally(width)
                                    ? width
                                    : window.innerWidth
                            );
                        } else {
                            this.setPopoverWidth(this.offsetWidth);
                        }
                        return this.popoverElement.offsetWidth;
                    },
                    horizontalOffset: () => {
                        if (
                            this.parentElement?.localName === 'ui-inputgroup' &&
                            this.hasCombinedInputSibling()
                        ) {
                            const groupRect =
                                this.parentElement.getBoundingClientRect();
                            const dropdownRect = this.getBoundingClientRect();

                            return groupRect.left - dropdownRect.left;
                        }
                        return this.popoverHOffset;
                    },
                    supportMiddleAlignment: true,
                }),
                target: this,
                onOpen: () => {
                    const options =
                        this.popoverElement.querySelector<HTMLUListElement>(
                            '.ui-dropdown__options'
                        );
                    const selected = this.getActiveOption();
                    const optionToFocus = selected
                        ? selected
                        : options?.querySelector<HTMLLIElement>(
                              '.ui-dropdown__option:not(.-hidden)'
                          );

                    // Looks like we encounter same or similar Apple VO bug. This is because
                    // we use requestAnimationFrame() here.
                    // https://bugs.webkit.org/show_bug.cgi?id=167671
                    requestAnimationFrame(() => optionToFocus?.focus());
                },
                onClose: () => (this.expanded = false),
            },
        });
    }

    /**
     * Checks if the dropdown should appear together with another control (in one box) in a ui-inputgroup.
     * @private
     * @returns {boolean}
     */
    private hasCombinedInputSibling(): boolean {
        return !!this.parentElement?.matches(
            ':has(> :is(input + ui-dropdown, ui-dropdown + select, select + ui-dropdown, ui-dropdown + ui-dropdown))'
        );
    }

    /**
     * Closes detached popover.
     */
    closeDetachedPopover(): void {
        if (!this._detachedPopover) {
            return;
        }
        const modal = this.modal; // query once
        if (modal) {
            this.removePopoverModalListener();

            const detachedPopover = this._detachedPopover;
            this._popoverModalToClose = modal;
            this._popoverModalToCloseHandler = (event: Event) => {
                const modalEvent = event as CustomEvent<{ action?: string }>;
                if (
                    modalEvent?.detail?.action ===
                    UIModalController.UpdateActions.Remove
                ) {
                    this.removePopoverModalListener();
                    if (this._detachedPopover === detachedPopover) {
                        this._detachedPopover = null;
                    }
                }
            };

            // Subscribe to modal update event to detect when popover is removed from DOM
            modal.addEventListener(
                'modal-updatelist',
                this._popoverModalToCloseHandler
            );
            // Close the modal, this will dispatch modal update event, with a clean up to follow
            modal.close();
        }
    }

    /**
     * Clears current input value.
     */
    private clearInputValue(): void {
        this._inputValue = '';
    }

    /**
     * Updates dropdown control when selected item is changed.
     * @returns {HTMLLIElement|undefined}
     */
    private updateSelected(): UndefinedOr<HTMLLIElement> {
        const allOptions = this.popoverElement.querySelectorAll<HTMLLIElement>(
            '.ui-dropdown__option'
        );
        const selectedLi = [...allOptions].find((option) =>
            option.classList.contains('-selected')
        );
        if (selectedLi) {
            selectedLi.removeAttribute('aria-selected');
            selectedLi.removeAttribute('aria-current');
            updateClassList(selectedLi, {
                '-selected': false,
                '-hidden': false,
            });
            selectedLi.firstElementChild?.removeAttribute('selected');
        }
        const option = allOptions[this.selectedIndex];
        if (option) {
            updateClassList(option, {
                '-selected': true,
                '-hidden': !!this.hideSelected,
            });
            option.setAttribute('aria-selected', 'true');
            option.setAttribute('aria-current', 'true');
            option.firstElementChild?.setAttribute('selected', '');
        }
        return option;
    }

    /**
     * Close popover element.
     */
    private closePopover(): void {
        this.removeAttribute('data-popover-align');
        this.expanded = false;
    }

    /**
     * Toggle popover visibility.
     * @private
     */
    private toggleDropdownPopover(): void {
        if (this.readonly || this.disabled) {
            this.expanded = false;
        } else {
            this.expanded = !this.expanded;
        }
    }

    /**
     * Builds the selected-option config for the control, tagging it with the value
     * element id when inside ui-field (for the aria-labelledby pattern).
     * @param {HTMLOptionElement} option
     * @param {HTMLSelectElement} [select]
     * @private
     */
    private buildSelectedControlConfig(
        option: HTMLOptionElement,
        select?: HTMLSelectElement
    ): ElementConfig {
        const config = this.renderOption(option, true);
        if (this.closest('ui-field') || this.getControlName()) {
            this.setConfigId(config, this.getValueElementId(select));
        }
        return config;
    }

    /**
     * Builds a visually hidden element config containing the control's accessible name, to be referred to from an aria-labelledby attribute.
     * @param {HTMLSelectElement} [select]
     * @returns {ElementConfig | null}
     * @private
     */
    private renderControlName(
        select?: HTMLSelectElement
    ): NullOr<ElementConfig> {
        const controlName = this.getControlName();
        if (!controlName) {
            return null;
        }

        return {
            tagName: 'span',
            attributes: {
                id: this.getControlNameElementId(select),
            },
            classList: {
                'ui-dropdown__control-name': true,
                '-visually-hidden': true,
            },
            children: [controlName],
        };
    }

    /**
     * Replaces the control's content with the element for the selected option.
     * @param {HTMLOptionElement} option
     * @private
     */
    private renderSelectedControl(option: HTMLOptionElement): void {
        this.control.innerHTML = '';
        insertElements(this.control, [this.renderControlName()]);
        this.control.appendChild(
            this.createElement(this.buildSelectedControlConfig(option))
        );
    }

    /**
     * Fires callback when the dropdown value has been changed.
     * @private
     */
    private onChangeValue(): void {
        const control = this.control;
        if (!control) {
            return;
        }

        const nativeSelect = this.nativeSelect;
        const selectedOption =
            nativeSelect.options[nativeSelect.options.selectedIndex];

        if (selectedOption) {
            this.renderSelectedControl(selectedOption);
            this.updateSelected();
        }

        this.closePopover();
    }

    /**
     * Fires callback when the popover state has been changed
     * @private
     */
    private onChangePopoverState(): void {
        if (this.isMobile() && this.mobileNative) {
            return;
        }
        const isActive = !!this.expanded;
        const popover = this.popoverElement;
        const control = this.control;
        if (!isActive && control) {
            control.removeAttribute('data-popover-align');
        }
        if (!popover || !control) {
            return;
        }

        control.setAttribute('aria-expanded', String(!!isActive));

        if (isActive) {
            show(popover);
            this.setPopoverHeight(this.getPopoverHeight());
            this.openDetachedPopover();
            this.attachOutsideClickHandler();
        } else {
            this.closeDetachedPopover();
            this.detachOutsideClickHandler();
            hide(popover);
        }

        this.expanded = isActive;
    }

    /**
     * Fires callback when changed between mobile and desktop
     * @private
     */
    private onChangeAppearance(): void {
        if (this.isMobile()) {
            hide(this.customSelect);
            show(this.nativeSelect);
        } else {
            show(this.customSelect);
            hide(this.nativeSelect);
            this.onChangePopoverState();
        }
    }

    /**
     * Fires callback when option renderer type is changed
     * @private
     */
    private onChangeOptionRenderer(): void {
        const nativeSelect = this.nativeSelect;
        const control = this.control;
        const popover = this.popoverElement;

        if (!nativeSelect || !control || !popover) {
            return;
        }
        const selectedOption =
            nativeSelect.querySelectorAll('option')[nativeSelect.selectedIndex];

        if (selectedOption) {
            this.renderSelectedControl(selectedOption);
        }

        const optionsList = popover.querySelector<HTMLElement>(
            '.ui-dropdown__options'
        );
        if (!optionsList) {
            return;
        }
        optionsList.innerHTML = '';

        insertElements(
            optionsList,
            Array.from(nativeSelect.querySelectorAll('option')).map((option) =>
                this.renderOptionWrapper(option)
            )
        );
    }

    /**
     * Fires callback when the native select's value has been changed.
     * @private
     */
    private handleNativeSelectChange(): void {
        // Prevent value changes when readonly
        if (this.readonly) {
            return;
        }
        this.onChangeValue();
    }

    /**
     * Fires callback when the select's parent form has been reset.
     * @private
     */
    private handleFormReset(): void {
        const selectedOption = [...this.nativeSelect.options]
            .filter((node) => node.defaultSelected)
            .shift();

        if (selectedOption) {
            this.nativeSelect.value = selectedOption.value;
        } else {
            this.nativeSelect.selectedIndex = 0;
        }
        this.onChangeValue();
    }

    /**
     * Fires callback when user clicked outside of dropdown element.
     * @param {MouseEvent} event
     * @private
     */
    private handleClickOutside(event?: Event): void {
        if (!event) {
            return;
        }
        const target = event.target as NullOr<HTMLElement>;

        const shouldBeClosedOutside =
            !!this.expanded && !!target && !this.isParentOf(target);
        if (shouldBeClosedOutside) {
            this.closePopover();
        }
    }

    /**
     * Fires callback when user clicked on option
     * @param {MouseEvent} event
     * @private
     */
    private handleClickOnOption(event: Event): void {
        // Prevent selection when readonly or disabled
        if (this.readonly || this.disabled) {
            event.preventDefault();
            return;
        }

        const target = event.target as NullOr<HTMLElement>;
        const targetOption = target?.closest<HTMLElement>(
            '.ui-dropdown__option, .ui-dropdown__group'
        );
        if (!targetOption) {
            return;
        }

        const link = targetOption.querySelector<HTMLAnchorElement>('a');
        if (link) {
            const href = link.href;
            const linkTarget = link.target;
            if (href) {
                linkTarget
                    ? window.open(href, linkTarget)
                    : (window.location.href = href);
                return;
            }
        }
        event.preventDefault();
        if (!target) {
            return;
        }
        const optionsRoot = target.closest('.ui-dropdown__options');
        if (!optionsRoot) {
            return;
        }
        const opts = optionsRoot.querySelectorAll('.ui-dropdown__option');
        const disallowToClick = ['-disabled', 'ui-dropdown__group'];
        if (
            disallowToClick.some((cls) => targetOption.classList.contains(cls))
        ) {
            return;
        }
        this.nativeSelect.selectedIndex =
            Array.from(opts).indexOf(targetOption);
        dispatchNativeEvent(this.nativeSelect, 'change');
        this.closePopover();
        this.control.focus();
    }

    /**
     * Fires callback when user presses tab, and up&down arrows. Handles keyboard interaction for the dropdown.
     * @param {KeyboardEvent} event
     * @private
     */
    private handleKeyDown(event: KeyboardEvent): void {
        // When dropdown is closed, handle control button keys
        if (!this.expanded) {
            // Don't allow opening dropdown when readonly or disabled
            if (this.readonly || this.disabled) {
                return;
            }

            // ENTER (WCAG keyboard navigation) or SPACE (native select behavior) opens the dropdown
            if (
                keyCodes.ENTER === event.keyCode ||
                keyCodes.SPACE === event.keyCode
            ) {
                event.preventDefault();
                this.expanded = true;
                return;
            }

            // Arrow keys can also open the dropdown when closed
            if (
                keyCodes.UP === event.keyCode ||
                keyCodes.DOWN === event.keyCode
            ) {
                event.preventDefault();
                this.expanded = true;
            }
            return;
        }

        // When dropdown is expanded, handle navigation keys
        if (
            keyCodes.ESCAPE === event.keyCode ||
            keyCodes.TAB === event.keyCode
        ) {
            this.closePopover();
            this.control.focus();
            return;
        }

        // ENTER or SPACE selects the focused option (WCAG ARIA Authoring Practices for listbox)
        if (
            keyCodes.ENTER === event.keyCode ||
            keyCodes.SPACE === event.keyCode
        ) {
            event.preventDefault();
            const focusedOption = document.activeElement as NullOr<HTMLElement>;
            if (focusedOption?.classList.contains('ui-dropdown__option')) {
                // Trigger click on the focused option to reuse existing selection logic
                focusedOption.click();
            }
            return;
        }

        const options = [...this.getVisibleOptions()];
        if (keyCodes.UP === event.keyCode || keyCodes.DOWN === event.keyCode) {
            const step = keyCodes.UP === event.keyCode ? -1 : 1;
            const currentIndex = options.indexOf(
                document.activeElement as HTMLLIElement
            );
            let nextIndex = currentIndex;

            options.some(() => {
                nextIndex = nextIndex + step;
                if (nextIndex < 0) {
                    nextIndex = options.length - 1;
                }
                if (nextIndex > options.length - 1) {
                    nextIndex = 0;
                }

                if (!options[nextIndex].classList.contains('-disabled')) {
                    options[nextIndex].focus();
                    return true;
                }
                return false;
            });
            event.preventDefault();
        }

        const inputKey = event.key;
        // match all except whitespace
        if (inputKey.length === 1 && /\S/i.test(inputKey)) {
            this.inputValue = this.inputValue + inputKey;
            const inputValue = this.inputValue;
            const nextOption = options.find((option) => {
                const results = [...inputValue].map((char, index) => {
                    return (
                        (option.textContent || '')
                            .charAt(index)
                            .toLowerCase() === char.toLowerCase()
                    );
                });
                return results.every(Boolean);
            });
            if (nextOption) {
                nextOption.focus();
            }

            event.preventDefault();
        }
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'expanded':
                this.onChangePopoverState();
                break;
            case 'option':
                this.onChangeOptionRenderer();
                break;
            case 'control-id':
                this.handleControlIdChange(newValue);
                break;
        }
    }

    /**
     * Updates native select, button id, aria-labelledby, and value element id when control-id changes.
     * @private
     * @param {string} [newControlId]
     */
    private handleControlIdChange(newControlId?: NullOr<string>): void {
        const select = this.resolveNativeSelect();
        if (select && !this._hasUserSetNativeId) {
            if (newControlId) {
                this.setDropdownOwnedNativeId(select, newControlId);
            } else {
                this.removeDropdownOwnedNativeId(select);
            }
        }

        this.syncFieldControlIdReferences();
    }

    /**
     * Returns the rendered label ID for the provided or closest ui-field.
     * @param {UIField | null} [field]
     * @private
     */
    private getFieldLabelId(
        field: NullOr<UIField> = this.closest<UIField>('ui-field')
    ): string {
        return (
            field
                ?.querySelector<HTMLElement>('.ui-field__label-inner')
                ?.getAttribute('id') || ''
        );
    }

    /**
     * Updates the ARIA references (control button, listbox,
     * and selected-value option element) based on changes to
     * external IDs (for example when consumer code sets a new id
     * on the slotted native select).
     * @private
     */
    syncFieldAriaReferences(): void {
        const labelId = this.getFieldLabelId();
        const controlName = this.getControlName();
        if (!labelId && !controlName) {
            return;
        }
        const valueId = this.getValueElementId();

        if (this.control) {
            const valueElement =
                this.control.querySelector<HTMLElement>('.-control');
            if (valueElement && valueId) {
                valueElement.setAttribute('id', valueId);
            }
            const controlNameId = this.syncControlNameElement();
            const ariaLabelledBy = controlNameId
                ? `${controlNameId} ${valueId}`
                : valueId
                  ? `${labelId} ${valueId}`
                  : labelId;
            this.control.setAttribute('aria-labelledby', ariaLabelledBy);
            this.control.removeAttribute('aria-label');
        }
        if (labelId && this.nativeSelect) {
            // aria-labelledby wins over aria-label, so defer to an author aria-label when present.
            if (this.getNativeSelectAriaLabel()) {
                this.nativeSelect.removeAttribute('aria-labelledby');
            } else {
                this.nativeSelect.setAttribute('aria-labelledby', labelId);
            }
        }
        if (labelId && this.options) {
            const nativeSelectAriaLabel = this.getNativeSelectAriaLabel();
            if (nativeSelectAriaLabel) {
                this.options.setAttribute('aria-label', nativeSelectAriaLabel);
                this.options.removeAttribute('aria-labelledby');
            } else {
                this.options.setAttribute('aria-labelledby', labelId);
                this.options.removeAttribute('aria-label');
            }
        }
    }

    /**
     * Updates dropdown.
     * @fires event:popover-updated
     */
    updateDropdown(): void {
        const select = this.querySelector('select');
        if (!select) {
            return;
        }
        const options = [...select.children] as Array<
            HTMLOptionElement | HTMLOptGroupElement
        >;

        const newOptions = this.createElement(
            this.renderOptionsWrapper(options, select.required, select)
        );
        const oldOptions = this.popoverElement.querySelector(
            '.ui-dropdown__options'
        );
        if (!oldOptions) {
            return;
        }
        prerenderElementsTree(oldOptions, true);
        rebuild(oldOptions, newOptions);

        const selectedIndex =
            select.options.selectedIndex > -1
                ? select.options.selectedIndex
                : 0;

        const newControlConfig = this.buildSelectedControlConfig(
            select.options[selectedIndex] || document.createElement('option'),
            select
        );

        const newControl = this.createElement(newControlConfig);
        const oldControl =
            this.control.querySelector<HTMLElement>('.-control') ||
            this.control.firstElementChild;
        if (!oldControl) {
            return;
        }
        prerenderElementsTree(oldControl, true);
        rebuild(oldControl, newControl);
        this.dispatchCustomEvent('popover-updated');
    }

    /**
     * Builds the config for the listbox wrapper holding all options and groups.
     * @param {Array<HTMLOptionElement|HTMLOptGroupElement>} options
     * @param {boolean} [required]
     * @param {HTMLSelectElement} [select]
     * @returns {ElementConfig}
     */
    renderOptionsWrapper(
        options: Array<HTMLOptionElement | HTMLOptGroupElement>,
        required = false,
        select?: HTMLSelectElement
    ): ElementConfig {
        const attributes = {
            'id': this.getListboxId(),
            'role': 'listbox',
            'aria-required': required ? 'true' : null,
            ...this.getListboxAriaLabel(select),
        };

        let groupIndex = 0;

        return {
            tagName: 'ul',
            attributes: attributes,
            classList: {
                'ui-dropdown__options': true,
            },
            children: options.map((option) => {
                if (option instanceof HTMLOptGroupElement) {
                    return this.renderOptionGroup(option, groupIndex++);
                }
                return this.renderOptionWrapper(option);
            }),
        };
    }

    /**
     * Builds the config for an option group.
     * @param {HTMLOptGroupElement} group
     * @param {number} groupIndex - Index of the group
     * @returns {ElementConfig}
     */
    renderOptionGroup(
        group: HTMLOptGroupElement,
        groupIndex: number
    ): ElementConfig {
        const groupLabelId = `ui-dropdown-group-${this.uid}-${groupIndex}`;
        const groupLabelElement = this.renderOption(group, false);

        // Add id to group label for aria-describedby reference
        this.setConfigId(groupLabelElement, groupLabelId);

        return {
            tagName: 'li',
            classList: {
                'ui-dropdown__group': true,
            },
            attributes: {
                role: 'group',
            },
            children: [
                groupLabelElement,
                {
                    tagName: 'ul',
                    attributes: {
                        role: 'presentation',
                    },
                    classList: {
                        'ui-dropdown__group-options': true,
                    },
                    children: [...group.children].map((node) =>
                        this.renderOptionWrapper(node as HTMLOptionElement, {
                            groupLabelId: groupLabelId,
                        })
                    ),
                },
            ],
        };
    }

    /**
     * Builds the config for a single option list item.
     * @param {HTMLOptionElement} option
     * @param {{groupLabelId: string}} [groupInfo] - Group information including groupLabelId
     * @param {string} [groupInfo.groupLabelId]
     * @returns {ElementConfig}
     */
    renderOptionWrapper(
        option: HTMLOptionElement,
        groupInfo?: { groupLabelId?: string }
    ): ElementConfig {
        const attributes: Attributes = {
            'role': 'option',
            'tabindex': TabIndex.Inactive,
            'aria-selected': option.selected ? 'true' : 'false',
            'aria-current': option.selected ? 'true' : null,
            'aria-disabled': option.disabled ? 'true' : null,
        };

        // If part of a group, reference the group label for additional context
        if (groupInfo?.groupLabelId) {
            attributes['aria-describedby'] = groupInfo.groupLabelId;
        }

        return {
            tagName: 'li',
            classList: {
                'ui-dropdown__option': true,
                '-selected': option.selected,
                '-disabled': option.disabled,
                '-hidden': !!this.hideSelected && option.selected,
            },
            attributes,
            children: [this.renderOption(option, false)],
        };
    }

    /**
     * Builds the config for an element that renders an option's content,
     * to use inside of a control button or as a popover list item.
     * @param {HTMLOptionElement | HTMLOptGroupElement} option
     * @param {boolean} control - Whether the option is for the control (selected value) or for the popover list
     * @returns {ElementConfig}
     */
    renderOption(
        option: HTMLOptionElement | HTMLOptGroupElement,
        control: boolean
    ): ElementConfig {
        const isGroup = option.localName === 'optgroup';
        const attributes: Attributes = {
            optgroup: isGroup ? true : null,
        };

        for (const key in option.dataset) {
            if (Object.prototype.hasOwnProperty.call(option.dataset, key)) {
                const dataValue = option.dataset[key];
                if (dataValue !== undefined) {
                    attributes[key] = dataValue;
                }
            }
        }

        if (isGroup) {
            attributes.text = option.getAttribute('label');
        } else {
            const optionElement = option as HTMLOptionElement;
            if (optionElement.hasAttribute('value')) {
                attributes.value = optionElement.value;
                const value = optionElement.value.toLowerCase();
                if (
                    this.layout === UIDropdown.layouts.LANG &&
                    UIDropdown.labels[value]
                ) {
                    const languageLabel = UIDropdown.labels[value];
                    if (typeof languageLabel === 'string') {
                        attributes['aria-label'] = languageLabel;
                    }
                }
            }

            if (optionElement.selected) {
                attributes.selected = true;
            }

            if (optionElement.disabled) {
                attributes.disabled = true;
            }

            if (optionElement.textContent !== '') {
                attributes.text = optionElement.textContent;
            }
        }

        if (this.layout === UIDropdown.layouts.LANG) {
            attributes.color = this.glyphColor || null;
        }

        attributes.control = control;
        attributes.selectable = (!control && !this.hideSelected) || null;
        return {
            tagName: this.option,
            attributes,
        };
    }

    /**
     * Handles observed native select mutations by synchronizing state, IDs,
     * rendering, and ARIA references as needed.
     * @param {Array<MutationRecord>} mutationRecord
     * @private
     */
    private handleControlMutations(
        mutationRecord: Array<MutationRecord>
    ): void {
        let needsDropdownUpdate = false;

        for (const mutation of mutationRecord) {
            const target = mutation.target as HTMLElement;

            // Handle disabled attribute on native select
            if (mutation.attributeName === 'disabled') {
                this.onDisabledStateChanged(
                    (target as HTMLSelectElement).disabled
                );
                needsDropdownUpdate = true;
            }

            // Handle readonly attribute on native select
            if (mutation.attributeName === 'readonly') {
                this.onReadonlyStateChanged(this.readonly);
                needsDropdownUpdate = true;
            }

            if (
                target === this.nativeSelect &&
                mutation.attributeName === 'id'
            ) {
                const id = this.nativeSelect.getAttribute('id')?.trim();
                this._hasUserSetNativeId =
                    !!id && id !== this._dropdownOwnedNativeId;
                this.syncFieldControlIdReferences();
                continue;
            }

            // For any other mutations on nativeSelect (like childList changes)
            if (
                target === this.nativeSelect &&
                !['disabled', 'readonly'].includes(mutation.attributeName || '')
            ) {
                needsDropdownUpdate = true;
            }
        }

        if (needsDropdownUpdate) {
            this.updateDropdown();
            this.syncFieldAriaReferences();
        }
    }

    /**
     * Returns a layout-specific fallback aria-label.
     * @param {Attributes} defaultLabel attributes to use when no specific label applies
     * @private
     * @returns {Attributes}
     */
    private getLayoutFallbackAriaLabel(
        defaultLabel: Attributes = {}
    ): Attributes {
        switch (this.layout) {
            case UIDropdown.layouts.DIAL:
                return {
                    'aria-label': UIDropdown.labels.countryCode,
                };
            case UIDropdown.layouts.LANG:
                return {
                    'aria-label': UIDropdown.labels.openLang,
                };
            default:
                return defaultLabel;
        }
    }

    /**
     * Returns the accessible name authored on the native select.
     * @param {HTMLSelectElement} [select]
     * @private
     * @returns {string}
     */
    private getNativeSelectAriaLabel(
        select: NullOr<HTMLSelectElement> = this.resolveNativeSelect()
    ): string {
        return select?.getAttribute('aria-label')?.trim() || '';
    }

    /**
     * Resolves the intent name for the control button. Prefers the host
     * <code>aria-label</code>; falls back to the deprecated <code>title-open</code>.
     * @private
     * @returns {string}
     */
    private getControlName(): string {
        return (
            this.getAttribute('aria-label') ||
            this.getAttribute('title-open') ||
            this.titleOpen ||
            ''
        );
    }

    /**
     * Returns the ID of an element containing the accessible name of the control button, derived from the native select ID. Otherwise returns effective control ID.
     * @param {HTMLSelectElement} [select]
     * @private
     */
    private getControlNameElementId(
        select: NullOr<HTMLSelectElement> = this.resolveNativeSelect()
    ): string {
        const controlId =
            select?.getAttribute('id')?.trim() || this.getControlIdForField();
        return `${controlId}${UIDropdown.CONTROL_NAME_ID_SUFFIX}`;
    }

    /**
     * Synchronizes the control-name element with the accessible name,
     * removing it when no control name is defined.
     * @private
     * @returns {string} The synchronized element ID, or an empty string when removed.
     */
    private syncControlNameElement(): string {
        const nameElement = this.control.querySelector<HTMLElement>(
            '.ui-dropdown__control-name'
        );

        const controlName = this.getControlName();
        if (!controlName) {
            nameElement?.remove();
            return '';
        }

        const nameId = this.getControlNameElementId();
        const syncedNameElement = nameElement || document.createElement('span');

        syncedNameElement.id = nameId;
        syncedNameElement.classList.add(
            'ui-dropdown__control-name',
            '-visually-hidden'
        );
        syncedNameElement.textContent = controlName;

        if (!nameElement) {
            this.control.prepend(syncedNameElement);
        }

        return nameId;
    }

    /**
     * Compute ARIA label for the control button.
     * When controlId exists, the ui-field's label element provides the accessible name
     * via aria-labelledby which is set in the render() method.
     * @param {HTMLSelectElement} [select]
     * @private
     * @returns {Record<string, string>}
     */
    private getControlAriaLabel(select?: HTMLSelectElement): Attributes {
        if (this.getControlName()) {
            return {
                'aria-labelledby': `${this.getControlNameElementId(select)} ${this.getValueElementId(select)}`,
            };
        }

        const field = this.closest<UIField>('ui-field');
        const labelId = this.getFieldLabelId(field);
        if (labelId) {
            return {
                'aria-labelledby': `${labelId} ${this.getValueElementId(select)}`,
            };
        }

        if (field?.label) {
            return {};
        }

        return this.getLayoutFallbackAriaLabel();
    }

    /**
     * Returns aria attributes for the main listbox element (options list).
     * @param {HTMLSelectElement} [select]
     * @private
     * @returns {Record<string, string>}
     */
    private getListboxAriaLabel(select?: HTMLSelectElement): Attributes {
        const nativeSelectAriaLabel = this.getNativeSelectAriaLabel(select);
        if (nativeSelectAriaLabel) {
            return { 'aria-label': nativeSelectAriaLabel };
        }

        // For the main listbox, when used inside ui-field, use the field label as
        // the accessible name via aria-labelledby.
        const field = this.closest<UIField>('ui-field');
        if (field?.getControlId) {
            const controlId = field.getControlId();
            if (controlId) {
                return {
                    'aria-labelledby': `${controlId}${UIDropdown.FIELD_LABEL_ID_SUFFIX}`,
                };
            }
        }

        // Fallback for basic dropdowns without explicit labels; the listbox needs
        // an accessible name, so a generic label is acceptable here.
        return this.getLayoutFallbackAriaLabel({
            'aria-label': UIDropdown.labels.listboxName,
        });
    }

    /**
     * Gets the value element ID with an index suffix if multiple dropdowns exist in the same ui-field.
     * @param {HTMLSelectElement} [select]
     * @private
     * @returns {string}
     */
    private getValueElementId(select?: HTMLSelectElement): string {
        let baseId = '';
        const field = this.closest<UIField>('ui-field');
        if (field?.getControlId) {
            baseId = field.getControlId() || '';

            if (!baseId) {
                return '';
            }

            const siblings = Array.from(field.querySelectorAll('ui-dropdown'));
            const baseIdWithSuffix = `${baseId}${UIDropdown.VALUE_ID_SUFFIX}`;
            if (siblings.length > 1) {
                const index = siblings.indexOf(this);
                return `${baseIdWithSuffix}-${index + 1}`;
            }

            return baseIdWithSuffix;
        }

        // Standalone dropdown: fallback to native select id or a generated uid.
        select ??= this.resolveNativeSelect() || undefined;
        const selectId = select?.getAttribute('id');
        baseId = selectId || this.uid;

        return `${baseId}${UIDropdown.VALUE_ID_SUFFIX}`;
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLSelectElement {
        return this.nativeSelect;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: AttributeValue): void {
        const value = typeof newValue === 'string' ? newValue : '';
        setNativeInputValue(this.nativeSelect, value);
        this.onChangeValue();
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.disabled = newState;
        this.control.disabled = newState;
        if (this.disabled) {
            this.expanded = false;
            this.classList.add('-disabled');
        } else {
            this.classList.remove('-disabled');
        }
    }

    /**
     * @inheritDoc
     */
    onReadonlyStateChanged(newState: boolean): void {
        if (newState) {
            this.expanded = false;
            this.classList.add('-readonly');
        } else {
            this.classList.remove('-readonly');
        }
    }

    /**
     * Observes native select changes.
     * @private
     */
    private observeNativeSelect(): void {
        this.observer ??= new MutationObserver(this.controlMutationsHandler);
        // NB: Attribute filtering is intentional. Reacting to every attribute would cause an infinite mutation loop.
        this.observer.observe(this.nativeSelect, {
            childList: true,
            subtree: true,
            characterData: true,
            attributeFilter: [
                'disabled',
                'readonly',
                'id',
                'required',
                'aria-label',
            ],
        });
    }

    /**
     * Registers the outside-click handler and discards stale asynchronous registrations.
     * @private
     */
    private attachOutsideClickHandler(): void {
        this.detachOutsideClickHandler();
        const requestId = ++this.outsideClickHandlerRequestId;

        void addOutsideClickHandler(this, this.outsideClickHandler).then(
            (outsideClickRemover) => {
                if (requestId !== this.outsideClickHandlerRequestId) {
                    outsideClickRemover();
                    return;
                }

                this.outsideClickRemover = outsideClickRemover;
            }
        );
    }

    /**
     * Invalidates pending outside-click registrations and removes the active handler.
     * @private
     */
    private detachOutsideClickHandler(): void {
        this.outsideClickHandlerRequestId++;
        this.outsideClickRemover?.();
        delete this.outsideClickRemover;
    }

    /** @private */
    private subscribeEvents(): void {
        this.options.addEventListener('click', this.optionClickHandler);
        this.nativeSelect.addEventListener(
            'change',
            this.nativeSelectChangeHandler
        );
        this.customSelect.addEventListener('keydown', this.keyDownHandler);
        this.options.addEventListener('keydown', this.keyDownHandler);
        this.control.addEventListener(
            'click',
            this.toggleDropdownPopoverHandler
        );

        if (this.mobileNative) {
            browser.mobileMedia?.addEventListener(
                'change',
                this.appearanceChangeHandler
            );
        }
        if (this.nativeSelect.form) {
            this.nativeSelectForm = this.nativeSelect.form;
            this.nativeSelectForm.addEventListener(
                'reset',
                this.formResetHandler
            );
        }
    }

    /** @private */
    private unsubscribeEvents(): void {
        this.options?.removeEventListener('click', this.optionClickHandler);
        this.nativeSelect?.removeEventListener(
            'change',
            this.nativeSelectChangeHandler
        );
        this.customSelect?.removeEventListener('keydown', this.keyDownHandler);
        this.options?.removeEventListener('keydown', this.keyDownHandler);
        this.control?.removeEventListener(
            'click',
            this.toggleDropdownPopoverHandler
        );

        browser.mobileMedia?.removeEventListener(
            'change',
            this.appearanceChangeHandler
        );
        this.nativeSelectForm?.removeEventListener(
            'reset',
            this.formResetHandler
        );
        delete this.nativeSelectForm;
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const node = this.detachChildNodes()
            .filter((node) => node?.localName === 'select')
            .shift() as UndefinedOr<HTMLSelectElement>;

        const select =
            node ||
            (this.createElement({
                tagName: 'select',
                children: [
                    {
                        tagName: 'option',
                    },
                ],
            }) as HTMLSelectElement);

        if (this._hasUserSetNativeId === undefined) {
            this._hasUserSetNativeId = !!select.getAttribute('id')?.trim();
        }
        let selectId = select.getAttribute('id');
        if (!selectId) {
            selectId =
                this.getAttribute('control-id') || this.controlId || this.uid;
            this.setDropdownOwnedNativeId(select, selectId);
        }
        const listboxId = UIDropdown.LISTBOX_ID_PREFIX + selectId;

        const options = [...select.options];
        const optionNodes = [...select.children] as Array<
            HTMLOptionElement | HTMLOptGroupElement
        >;
        const index = select.options.selectedIndex;

        // Button attributes
        const buttonAttributes = {
            'type': 'button',
            // ID is not strictly needed internally, but we keep it as a public API helper
            'id': `${selectId}${UIDropdown.CONTROL_BUTTON_ID_SUFFIX}`,
            'class': 'ui-dropdown__control',
            'aria-haspopup': 'listbox',
            'aria-expanded': 'false',
            // Uses the local selectId instead of getListboxId() because the select is
            // still detached here, so getControlIdForField() cannot resolve it yet.
            'aria-controls': listboxId,
            'tabindex': TabIndex.Active,
            ...this.getControlAriaLabel(select),
        };

        // Prepare the selected option element.
        // Only set an id when inside ui-field (for aria-labelledby reference).
        const selectedOptionConfig = this.buildSelectedControlConfig(
            options[index] || document.createElement('option'),
            select
        );

        this.insertElements([
            {
                element: select,
                classList: {
                    'ui-dropdown__select': true,
                },
                // aria-labelledby for the hidden native select is applied in hydrate()
                // once we know for sure whether we are inside a ui-field and have a
                // field label id available.
                attributes: {},
            },
            {
                tagName: 'div',
                classList: {
                    'ui-dropdown__custom': true,
                },
                children: [
                    {
                        tagName: 'button',
                        attributes: buttonAttributes,
                        children: [
                            this.renderControlName(select),
                            selectedOptionConfig,
                        ],
                    },
                    {
                        tagName: 'div',
                        classList: {
                            'ui-dropdown__popover': true,
                        },
                        children: [
                            this.renderOptionsWrapper(
                                optionNodes,
                                select.required,
                                select
                            ),
                        ],
                    },
                ],
            },
        ]);

        hide(select);
        hide(this.popoverElement);

        const disabled = this.nativeSelect.disabled;
        if (disabled) {
            this.classList.add('-disabled');
            this.control.disabled = true;
        }

        if (this.readonly) {
            this.classList.add('-readonly');
        }
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        appendModalControllerIfRequired();

        if (getDebugMode() && this.hasAttribute('title-open')) {
            warnForAttributeDeprecation(this, 'title-open');
        }

        this.subscribeEvents();

        if (this.mobileNative) {
            this.onChangeAppearance();
        } else {
            hide(this.nativeSelect);
        }
        this.observeNativeSelect();
        this.handleControlIdChange(this.getAttribute('control-id'));

        this.updateDropdown();

        this.clearInputValueDebounce = debounce(
            () => this.clearInputValue(),
            500
        );
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        const modal = this.modal;
        const hasPendingDetachedPopoverCleanup =
            !!this._popoverModalToCloseHandler;

        this.removePopoverModalListener();
        if (!hasPendingDetachedPopoverCleanup) {
            modal?.close();
        }
        this._detachedPopover = null;

        this.detachOutsideClickHandler();
        this.unsubscribeEvents();
        this.observer?.disconnect();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
        this.observeNativeSelect();
        if (this.mobileNative) {
            this.onChangeAppearance();
        }
    }
}
